import React, { useState } from 'react';
import { Phone, MessageSquare, Send, Mic, Volume2, Shield, Check, Copy, ExternalLink, AlertTriangle } from 'lucide-react';

export default function VoiceSetup() {
  const [config, setConfig] = useState({
    twilio: { accountSid: '', authToken: '', phoneNumber: '', yourPhone: '' },
    elevenlabs: { apiKey: '', voiceId: 'EXAVITQu4vr4xnSDxMaL' },
    telegram: { botToken: '', yourChatId: '' },
    slack: { botToken: '', yourUserId: '' }
  });

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, boolean>>({});

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const testChannel = async (channel: string) => {
    // Simulate test
    setTestResults({ ...testResults, [channel]: true });
  };

  const saveConfig = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'voice-config.json';
    a.click();
  };

  const voices = [
    { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella', type: 'Female - Friendly' },
    { id: 'ErXwobaYiN019PkySvjV', name: 'Antoni', type: 'Male - Professional' },
    { id: 'MF3mGyEYCl7XYWbV9V6O', name: 'Elli', type: 'Female - Young' },
    { id: 'TxGEqnHWrfWFTfGW9XjX', name: 'Josh', type: 'Male - Deep' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 text-white">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center items-center gap-3 mb-4">
            <Phone className="w-10 h-10 text-blue-400" />
            <Mic className="w-10 h-10 text-green-400" />
            <MessageSquare className="w-10 h-10 text-purple-400" />
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Claude Voice System Setup
          </h1>
          <p className="text-gray-400 text-lg">
            Enable Claude to contact you via voice across all channels
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {[
            { name: 'Twilio', url: 'https://console.twilio.com', icon: Phone },
            { name: 'ElevenLabs', url: 'https://elevenlabs.io', icon: Volume2 },
            { name: 'Telegram Bot', url: 'https://t.me/botfather', icon: Send },
            { name: 'Slack Apps', url: 'https://api.slack.com/apps', icon: MessageSquare }
          ].map(service => (
            <a
              key={service.name}
              href={service.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:bg-gray-700/50 transition-colors flex items-center gap-3"
            >
              <service.icon className="w-5 h-5 text-blue-400" />
              <span>{service.name}</span>
              <ExternalLink className="w-4 h-4 ml-auto text-gray-500" />
            </a>
          ))}
        </div>

        {/* Configuration Form */}
        <div className="space-y-6">
          {/* Twilio Section */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Phone className="w-6 h-6 text-blue-400" />
              Twilio Configuration (Phone Calls)
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Account SID</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="flex-1 bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                    value={config.twilio.accountSid}
                    onChange={(e) => setConfig({
                      ...config,
                      twilio: { ...config.twilio, accountSid: e.target.value }
                    })}
                  />
                  <button
                    onClick={() => copyToClipboard(config.twilio.accountSid, 'twilio-sid')}
                    className="px-3 py-2 bg-gray-700 rounded hover:bg-gray-600"
                  >
                    {copiedField === 'twilio-sid' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Auth Token</label>
                <input
                  type="password"
                  placeholder="Your auth token"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.twilio.authToken}
                  onChange={(e) => setConfig({
                    ...config,
                    twilio: { ...config.twilio, authToken: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Twilio Phone Number</label>
                <input
                  type="tel"
                  placeholder="+12345678900"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.twilio.phoneNumber}
                  onChange={(e) => setConfig({
                    ...config,
                    twilio: { ...config.twilio, phoneNumber: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Your Phone Number</label>
                <input
                  type="tel"
                  placeholder="+19876543210"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.twilio.yourPhone}
                  onChange={(e) => setConfig({
                    ...config,
                    twilio: { ...config.twilio, yourPhone: e.target.value }
                  })}
                />
              </div>
            </div>

            <button
              onClick={() => testChannel('twilio')}
              className="mt-4 px-4 py-2 bg-blue-600 rounded hover:bg-blue-500 transition-colors"
            >
              {testResults.twilio ? '✅ Tested' : 'Test Connection'}
            </button>
          </div>

          {/* ElevenLabs Section */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Volume2 className="w-6 h-6 text-green-400" />
              ElevenLabs Configuration (Voice Generation)
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">API Key</label>
                <input
                  type="password"
                  placeholder="Your ElevenLabs API key"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.elevenlabs.apiKey}
                  onChange={(e) => setConfig({
                    ...config,
                    elevenlabs: { ...config.elevenlabs, apiKey: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Voice Selection</label>
                <select
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.elevenlabs.voiceId}
                  onChange={(e) => setConfig({
                    ...config,
                    elevenlabs: { ...config.elevenlabs, voiceId: e.target.value }
                  })}
                >
                  {voices.map(voice => (
                    <option key={voice.id} value={voice.id}>
                      {voice.name} - {voice.type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={() => testChannel('elevenlabs')}
              className="mt-4 px-4 py-2 bg-green-600 rounded hover:bg-green-500 transition-colors"
            >
              {testResults.elevenlabs ? '✅ Tested' : 'Test Voice'}
            </button>
          </div>

          {/* Telegram Section */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Send className="w-6 h-6 text-purple-400" />
              Telegram Configuration (Optional)
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Bot Token</label>
                <input
                  type="text"
                  placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.telegram.botToken}
                  onChange={(e) => setConfig({
                    ...config,
                    telegram: { ...config.telegram, botToken: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Your Chat ID</label>
                <input
                  type="text"
                  placeholder="987654321"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.telegram.yourChatId}
                  onChange={(e) => setConfig({
                    ...config,
                    telegram: { ...config.telegram, yourChatId: e.target.value }
                  })}
                />
              </div>
            </div>

            <button
              onClick={() => testChannel('telegram')}
              className="mt-4 px-4 py-2 bg-purple-600 rounded hover:bg-purple-500 transition-colors"
            >
              {testResults.telegram ? '✅ Tested' : 'Test Bot'}
            </button>
          </div>

          {/* Slack Section */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-orange-400" />
              Slack Configuration
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Bot Token</label>
                <input
                  type="text"
                  placeholder="xoxb-your-bot-token"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.slack.botToken}
                  onChange={(e) => setConfig({
                    ...config,
                    slack: { ...config.slack, botToken: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Your User ID</label>
                <input
                  type="text"
                  placeholder="U1234567890"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={config.slack.yourUserId}
                  onChange={(e) => setConfig({
                    ...config,
                    slack: { ...config.slack, yourUserId: e.target.value }
                  })}
                />
              </div>
            </div>

            <button
              onClick={() => testChannel('slack')}
              className="mt-4 px-4 py-2 bg-orange-600 rounded hover:bg-orange-500 transition-colors"
            >
              {testResults.slack ? '✅ Tested' : 'Test Slack'}
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-center gap-4">
          <button
            onClick={saveConfig}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-semibold hover:from-purple-500 hover:to-pink-500 transition-colors"
          >
            Download Config File
          </button>
          <button
            className="px-6 py-3 bg-gray-700 rounded-lg font-semibold hover:bg-gray-600 transition-colors"
          >
            Upload to Cloudflare
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-12 bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Setup Instructions
          </h3>
          <ol className="space-y-2 text-gray-300">
            <li>1. Get your API keys from each service using the links above</li>
            <li>2. Fill in all required fields in the form</li>
            <li>3. Test each service to ensure connectivity</li>
            <li>4. Download the config file and save it as <code className="bg-gray-800 px-2 py-1 rounded">voice-config.json</code></li>
            <li>5. Run <code className="bg-gray-800 px-2 py-1 rounded">node scripts/setup-voice-secrets.js</code> to complete setup</li>
          </ol>
        </div>

        {/* Security Note */}
        <div className="mt-6 bg-red-900/20 border border-red-600/30 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-400" />
            <p className="text-sm text-gray-300">
              <strong>Security:</strong> Never commit your config file to Git. Add <code className="bg-gray-800 px-2 py-1 rounded">voice-config.json</code> to your <code className="bg-gray-800 px-2 py-1 rounded">.gitignore</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}